import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createSolutionPurchase } from '@/services/revolutService';
import RevolutCheckoutLoader from '@revolut/checkout';
import type { RevolutCheckoutInstance, Mode, RevolutCheckoutError } from '@revolut/checkout';
import { 
  MessageSquare, 
  Calendar, 
  Building2, 
  TrendingUp, 
  User, 
  Phone,
  ArrowRight,
  Workflow,
  Bot,
  Database,
  Zap,
  FileText,
  Sparkles,
  DollarSign,
  Target,
  CheckCircle2,
  ArrowLeft,
  CreditCard,
  Clock,
  Shield,
  Headphones
} from 'lucide-react';

// Solution data with icons
const solutionIcons: { [key: string]: React.ComponentType<{ className?: string }> } = {
  'content-creation-assistant': FileText,
  'support-genius': MessageSquare,
  'auto-scheduler-pro': Calendar,
  'knowledge-hub-assistant': Building2,
  'financial-insights-coach': TrendingUp,
  'personal-productivity-partner': User,
  'lead-conversion-agent': Target,
};

const solutionNodes: { [key: string]: { type: string; label: string; icon: React.ComponentType<{ className?: string }> }[] } = {
  'content-creation-assistant': [
    { type: 'trigger', label: 'Request', icon: MessageSquare },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'workflow', label: 'Create', icon: Sparkles },
    { type: 'action', label: 'Publish', icon: FileText }
  ],
  'support-genius': [
    { type: 'trigger', label: 'WhatsApp | Telegram', icon: MessageSquare },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'data', label: 'Knowledge Base', icon: Database },
    { type: 'action', label: 'Response', icon: MessageSquare }
  ],
  'auto-scheduler-pro': [
    { type: 'trigger', label: 'WhatsApp | Telegram | Phone', icon: Phone },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'action', label: 'Calendar', icon: Calendar },
    { type: 'notification', label: 'Reminder', icon: Zap }
  ],
  'knowledge-hub-assistant': [
    { type: 'trigger', label: 'Employee Query', icon: MessageSquare },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'data', label: 'Company Knowledge', icon: Database },
    { type: 'action', label: 'Answer', icon: MessageSquare }
  ],
  'financial-insights-coach': [
    { type: 'trigger', label: 'WhatsApp | Telegram', icon: MessageSquare },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'data', label: 'Financial Data', icon: Database },
    { type: 'action', label: 'Insights & Reports', icon: TrendingUp }
  ],
  'personal-productivity-partner': [
    { type: 'trigger', label: 'User Request', icon: MessageSquare },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'workflow', label: 'Multi-step', icon: Workflow },
    { type: 'action', label: 'Execute', icon: Zap }
  ],
  'lead-conversion-agent': [
    { type: 'trigger', label: 'Lead Data', icon: Database },
    { type: 'ai', label: 'AI Agent', icon: Bot },
    { type: 'workflow', label: 'Nurture', icon: Workflow },
    { type: 'action', label: 'Convert', icon: DollarSign }
  ],
};

const SolutionDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { solutionId } = useParams<{ solutionId: string }>();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gdprConsent: false
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Map solution IDs to translation keys
  const solutionTranslationMap: { [key: string]: string } = {
    'content-creation-assistant': 'contentCreation',
    'support-genius': 'supportGenius',
    'auto-scheduler-pro': 'autoScheduler',
    'knowledge-hub-assistant': 'knowledgeHub',
    'financial-insights-coach': 'financialInsights',
    'personal-productivity-partner': 'personalProductivity',
    'lead-conversion-agent': 'leadConversion',
  };

  const translationKey = solutionId ? solutionTranslationMap[solutionId] : null;
  
  if (!translationKey || !solutionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">{t('solutionDetail.notFound.title')}</h1>
          <p className="text-white/70 mb-8">{t('solutionDetail.notFound.description')}</p>
          <button
            onClick={() => navigate('/solutions')}
            className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-6 py-3 rounded-xl font-semibold"
          >
            {t('solutionDetail.notFound.backButton')}
          </button>
        </div>
      </div>
    );
  }

  const IconComponent = solutionIcons[solutionId] || Bot;
  const nodes = solutionNodes[solutionId] || [];
  const solutionName = t(`portfolio.items.${translationKey}.title`);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBuyNow = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setFieldErrors({});
    setErrorMessage('');
    
    // Validate form fields
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = t('institutional.booking.validationRequired');
    }
    
    if (!formData.email.trim()) {
      errors.email = t('institutional.booking.validationRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('institutional.booking.validationEmail');
    }
    
    if (!formData.gdprConsent) {
      errors.gdprConsent = t('institutional.booking.validationRequired');
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setFormStatus('processing');
      setErrorMessage('');

      // Create Revolut order for solution purchase
      const purchaseResponse = await createSolutionPurchase({
        name: formData.name,
        email: formData.email,
        solution_id: solutionId,
        solution_name: solutionName
      });

      if (!purchaseResponse.success || !purchaseResponse.token) {
        throw new Error(purchaseResponse.message || 'Failed to create purchase');
      }

      // Use mode from backend response
      let revolutMode: Mode = 'sandbox';
      
      if (purchaseResponse.mode === 'sandbox' || purchaseResponse.mode === 'prod') {
        revolutMode = purchaseResponse.mode as Mode;
      } else if (import.meta.env.VITE_REVOLUT_MODE === 'sandbox' || 
                 import.meta.env.VITE_REVOLUT_MODE === 'prod') {
        revolutMode = import.meta.env.VITE_REVOLUT_MODE as Mode;
      }
      
      // Load Revolut Checkout
      const instance: RevolutCheckoutInstance = await RevolutCheckoutLoader(
        purchaseResponse.token!,
        revolutMode
      );

      // Open payment popup
      instance.payWithPopup({
        name: formData.name,
        email: formData.email,
        savePaymentMethodFor: 'merchant',
        onSuccess: () => {
          instance.destroy();
          // Redirect to activation session scheduling
          setTimeout(() => {
            if (purchaseResponse.order_id) {
              navigate(`/activation-session/${purchaseResponse.order_id}?email=${encodeURIComponent(formData.email)}`);
            } else {
              setFormStatus('success');
              setFormData({
                name: '',
                email: '',
                gdprConsent: false
              });
            }
          }, 1000);
        },
        onError: (error: RevolutCheckoutError) => {
          setFormStatus('error');
          setErrorMessage(error.message || 'Payment failed. Please try again.');
          instance.destroy();
        },
        onCancel: () => {
          setFormStatus('idle');
          instance.destroy();
        }
      });

    } catch (error: unknown) {
      console.error('Purchase error:', error);
      setFormStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.');
    }
  };

  const nodeColors: { [key: string]: string } = {
    trigger: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
    ai: 'bg-brand-secondary/20 border-brand-secondary/50 text-brand-secondary',
    action: 'bg-green-500/20 border-green-500/50 text-green-400',
    data: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
    notification: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
    workflow: 'bg-pink-500/20 border-pink-500/50 text-pink-400'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <Header />

      <main className="relative pt-28 pb-12">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/solutions"
            className="inline-flex items-center text-brand-secondary hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('solutionDetail.backToStore')}
          </Link>

          {/* Hero Section */}
          <div className="relative backdrop-blur-lg rounded-3xl border border-brand-secondary/30 bg-gradient-to-br from-brand-secondary/5 to-brand-primary/5 p-8 sm:p-12 mb-8">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Left Side - Product Info */}
              <div className="flex-1">
                {/* Icon */}
                <div className="relative w-20 h-20 bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 rounded-2xl flex items-center justify-center mb-6">
                  <IconComponent className="w-10 h-10 text-brand-secondary" />
                </div>

                {/* Title */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {solutionName}
                </h1>

                {/* Tagline */}
                <p className="text-xl text-brand-secondary font-semibold mb-6">
                  {t(`portfolio.items.${translationKey}.tagline`)}
                </p>

                {/* Problem/Description */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-white mb-3">{t('solutionDetail.problemSolved')}</h2>
                  <p className="text-white/70 text-lg leading-relaxed">
                    {t(`portfolio.items.${translationKey}.description`)}
                  </p>
                </div>

                {/* Workflow Diagram */}
                <div className="relative bg-slate-900/50 rounded-xl p-4 sm:p-6 border border-white/10 mb-8">
                  <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">{t('solutionDetail.howItWorks')}</h3>
                  <div className="flex items-center justify-between gap-2 sm:gap-4">
                    {nodes.map((node, idx) => {
                      const NodeIcon = node.icon;
                      return (
                        <React.Fragment key={idx}>
                          <div className={`flex flex-col items-center gap-2 p-2 sm:p-3 rounded-lg border flex-1 min-w-0 ${nodeColors[node.type] || 'bg-white/5 border-white/20'}`}>
                            <NodeIcon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                            <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{node.label}</span>
                          </div>
                          {idx < nodes.length - 1 && (
                            <ArrowRight className="w-4 h-4 text-white/40 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Side - Pricing Card with Form */}
              <div className="lg:w-96">
                <div className="sticky top-28 bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-2xl border border-brand-secondary/40 p-6 sm:p-8">
                  {/* Plug & Play Badge */}
                  <div className="inline-flex items-center px-3 py-1.5 bg-brand-secondary/20 border border-brand-secondary/50 rounded-full mb-6">
                    <Zap className="w-4 h-4 text-brand-secondary mr-2" />
                    <span className="text-brand-secondary text-sm font-semibold">{t('solutionDetail.plugAndPlay')}</span>
                  </div>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl sm:text-5xl font-bold text-white">€299</span>
                      <span className="text-white/60">{t('solutionDetail.oneTime')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-lg">
                      <span className="text-white">+</span>
                      <span className="text-2xl font-bold text-brand-secondary">€149</span>
                      <span className="text-white/60">{t('solutionDetail.perMonth')}</span>
                    </div>
                  </div>

                  {/* Purchase Form */}
                  {formStatus === 'success' ? (
                    <div className="bg-brand-secondary/10 border border-brand-secondary/50 rounded-xl p-6 text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-brand-secondary to-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-white font-medium mb-2">{t('solutionDetail.successTitle')}</p>
                      <p className="text-white/70 text-sm">{t('solutionDetail.successMessage')}</p>
                    </div>
                  ) : formStatus === 'error' ? (
                    <div className="mb-6">
                      <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-white font-semibold text-sm mb-1">{t('institutional.booking.errorTitle')}</h4>
                            <p className="text-white/70 text-xs">{errorMessage}</p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setFormStatus('idle');
                          setErrorMessage('');
                        }}
                        className="w-full bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                      >
                        {t('institutional.booking.tryAgain')}
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleBuyNow} noValidate className="space-y-4 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-white/90 mb-2">
                          {t('institutional.booking.nameLabel')} <span className="text-brand-secondary">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:border-transparent transition-all duration-200 ${
                            fieldErrors.name ? 'border-red-500/50' : 'border-white/20'
                          }`}
                          placeholder={t('institutional.booking.namePlaceholder')}
                        />
                        {fieldErrors.name && (
                          <p className="mt-1 text-xs text-red-400">{fieldErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-white/90 mb-2">
                          {t('institutional.booking.emailLabel')} <span className="text-brand-secondary">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:border-transparent transition-all duration-200 ${
                            fieldErrors.email ? 'border-red-500/50' : 'border-white/20'
                          }`}
                          placeholder={t('institutional.booking.emailPlaceholder')}
                        />
                        {fieldErrors.email && (
                          <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
                        )}
                      </div>

                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="gdprConsent"
                          name="gdprConsent"
                          checked={formData.gdprConsent}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-brand-secondary bg-white/5 border-white/20 rounded focus:ring-brand-secondary focus:ring-2 mt-1"
                        />
                        <label htmlFor="gdprConsent" className="ml-2 text-xs text-white/60">
                          {t('institutional.booking.gdprLabel')}{' '}
                          <Link to="/privacy" target="_blank" rel="noreferrer" className="text-brand-secondary hover:text-brand-primary underline">
                            {t('institutional.booking.privacyLink')}
                          </Link>
                          {' '}{t('institutional.booking.and')}{' '}
                          <Link to="/terms" target="_blank" rel="noreferrer" className="text-brand-secondary hover:text-brand-primary underline">
                            {t('institutional.booking.termsLink')}
                          </Link>
                        </label>
                      </div>
                      {fieldErrors.gdprConsent && (
                        <p className="text-xs text-red-400">{fieldErrors.gdprConsent}</p>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'processing'}
                        className="w-full relative group bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-8 py-4 rounded-xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/60 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          {formStatus === 'processing' ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('institutional.booking.processing')}
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              {t('solutionDetail.buyButton')} — €299
                            </>
                          )}
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </form>
                  )}

                  {/* What's Included */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">{t('solutionDetail.whatsIncluded')}</h4>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{t('solutionDetail.included.activation')}</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{t('solutionDetail.included.setup')}</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{t('solutionDetail.included.support')}</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{t('solutionDetail.included.updates')}</span>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-center gap-6">
                    <div className="flex items-center gap-2 text-white/50 text-xs">
                      <Shield className="w-4 h-4" />
                      <span>{t('solutionDetail.secure')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/50 text-xs">
                      <Clock className="w-4 h-4" />
                      <span>{t('solutionDetail.fastSetup')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How the Process Works */}
          <div className="backdrop-blur-lg rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/10 p-8 sm:p-12 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">{t('solutionDetail.process.title')}</h2>
            
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="relative">
                <div className="absolute -left-3 top-0 w-8 h-8 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                <div className="pl-8">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('solutionDetail.process.step1.title')}</h3>
                  <p className="text-white/60 text-sm">{t('solutionDetail.process.step1.description')}</p>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute -left-3 top-0 w-8 h-8 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                <div className="pl-8">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('solutionDetail.process.step2.title')}</h3>
                  <p className="text-white/60 text-sm">{t('solutionDetail.process.step2.description')}</p>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute -left-3 top-0 w-8 h-8 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                <div className="pl-8">
                  <h3 className="text-lg font-semibold text-white mb-2">{t('solutionDetail.process.step3.title')}</h3>
                  <p className="text-white/60 text-sm">{t('solutionDetail.process.step3.description')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Need a Custom Solution Section */}
          <section className="relative">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-3xl blur-xl opacity-40 group-hover:opacity-60 transition duration-1000"></div>
              
              <div className="relative bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-8 sm:p-10 border border-brand-secondary/30 text-center">
                <div className="inline-flex items-center px-4 py-2 bg-brand-primary/20 border border-brand-secondary/50 rounded-full mb-6">
                  <Headphones className="w-4 h-4 text-brand-secondary mr-2" />
                  <span className="text-brand-secondary text-sm font-medium">{t('solutionDetail.customSolution.badge')}</span>
                </div>
                
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  {t('solutionDetail.customSolution.title')}
                </h2>
                <p className="text-white/70 text-lg mb-6 max-w-2xl mx-auto">
                  {t('solutionDetail.customSolution.description')}
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                  <div className="text-white/60 text-sm">
                    <span className="text-white font-semibold">{t('solutionDetail.customSolution.implementationFee')}</span>
                  </div>
                  <div className="hidden sm:block w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="text-white/60 text-sm">
                    <span className="text-white font-semibold">{t('solutionDetail.customSolution.monthlyFee')}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/custom-solutions')}
                  className="relative group/btn bg-gradient-to-r from-brand-secondary to-brand-primary text-white px-8 py-4 rounded-xl font-bold text-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/50 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {t('solutionDetail.customSolution.button')}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SolutionDetail;
